import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, TrendingUp, Briefcase, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/ui/use-toast';

const Newsletter = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "🚧 This feature isn't implemented yet—but don't worry! You can request it in your next prompt! 🚀",
    });
  };

  const benefits = [
    {
      icon: Briefcase,
      title: 'Service Updates',
      description: 'Be the first to hear about new IT services, capabilities, and offerings from our team',
    },
    {
      icon: TrendingUp,
      title: 'Industry Insights',
      description: 'Stay informed on enterprise IT trends, security advisories, and infrastructure best practices',
    },
    {
      icon: BookOpen,
      title: 'Technical Guides',
      description: 'Practical guides and case studies from our engineers on data center, networking, and AV topics',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Newsletter - Tasks IT Services | IT Infrastructure Insights</title>
        <meta name="description" content="Subscribe to the Tasks IT Services newsletter for IT industry insights, service updates, technical guides, and enterprise infrastructure best practices." />
      </Helmet>

      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full mb-6">
              <Mail className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl font-bold mb-6">
              Stay <span className="gradient-text">Informed</span>
            </h1>
            <p className="text-xl text-gray-600">
              Join IT leaders and enterprise managers receiving monthly insights on infrastructure, security, and AV technology
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-8 rounded-2xl shadow-xl mb-12"
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  className="text-lg"
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-lg py-6">
                Subscribe Now
              </Button>
              <p className="text-sm text-gray-500 text-center">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-center mb-8">What You'll Receive</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-lg text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full mb-4">
                    <benefit.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white p-8 rounded-xl shadow-lg"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">Why Subscribe?</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Monthly IT Digest:</span> Curated enterprise IT news and regulatory updates relevant to Japan
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Infrastructure Trends:</span> Analysis of emerging technologies in networking, data centers, and AV
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Case Studies:</span> Real-world examples of how we've solved complex IT challenges for enterprise clients
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Security Advisories:</span> Timely alerts and hardening tips to protect your network infrastructure
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <span className="font-semibold">Service Announcements:</span> First access to new services, promotions, and partnership opportunities
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Newsletter;